import theme from './theme';
import { ThemeProvider } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { useSelector, useDispatch } from 'react-redux';
import { Routes, Route, useLocation, matchPath } from 'react-router-dom';

// Pages
import News from './pages/News';
import Home from './pages/Home';
import About from './pages/About';
import Events from './pages/Events';
import History from './pages/History';
import Contact from './pages/Contact';
import Gallery from './pages/Gallery';
import NewsDetail from './pages/NewsDetail';
import Governance from './pages/Governance';
import Regulations from './pages/Regulations';
import GalleryDetails from './pages/GalleryDetails';
import WhatIsGymkhana from './pages/WhatIsGymkhana';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import SmoothScroll from './components/SmoothScroll';
import { useDebouncedEffect } from './hooks/useDebouncedEffect';

import { fetchLatestNews, selectNewsStatus } from './redux/newsSlice';
import { fetchContacts, selectContactStatus } from './redux/contactSlice';
import { fetchSliderItems, selectSliderStatus } from './redux/sliderSlice';
import { fetchAboutContent, selectAboutStatus } from './redux/aboutSlice';
import { fetchGalleryItems, selectGalleryStatus } from './redux/gallerySlice';
import { fetchHistoryItems, selectHistoryData } from './redux/historySlice';
import { fetchUpcomingEvents, selectEventsStatus } from './redux/eventsSlice';
import { fetchChampionshipStats, selectStatsStatus } from './redux/statsSlice';

const apiList = [
  { name: "news", api: fetchLatestNews, selector: selectNewsStatus },
  { name: "slider", api: fetchSliderItems, selector: selectSliderStatus },
  { name: "about", api: fetchAboutContent, selector: selectAboutStatus },
  { name: "gallery", api: fetchGalleryItems, selector: selectGalleryStatus },
  { name: "history", api: fetchHistoryItems, selector: (state) => selectHistoryData(state).status },
  { name: "events", api: fetchUpcomingEvents, selector: selectEventsStatus },
  { name: "stats", api: fetchChampionshipStats, selector: selectStatsStatus },
  { name: "contact", api: fetchContacts, selector: selectContactStatus },
];

const pageList = [
  { path: "/", name: "home", apis: ["slider", "news", "events", "stats"], element: <Home /> },
  { path: "/news", name: "news", apis: ["news"], element: <News /> },
  { path: "/about", name: "about", apis: ["about"], element: <About /> },
  { path: "/events", name: "events", apis: ["events"], element: <Events /> },
  { path: "/gallery", name: "gallery", apis: ["gallery"], element: <Gallery /> },
  { path: "/contact", name: "contact", apis: ["contact"], element: <Contact /> },
  { path: "/history", name: "history", apis: ["history"], element: <History /> },
  { path: "/news/:id", name: "news-detail", apis: ["news"], element: <NewsDetail /> },
  { path: "/governance", name: "governance", apis: ["about"], element: <Governance /> },
  { path: "/regulations", name: "regulations", apis: ["about"], element: <Regulations /> },
  { path: "/gallery/:category", name: "gallery-category", apis: ["gallery"], element: <Gallery /> },
  { path: "/what-is-gymkhana", name: "what-is-gymkhana", apis: ["about"], element: <WhatIsGymkhana /> },
  { path: "/regulations/:categoryId", name: "regulations-detail", apis: ["about"], element: <Regulations /> },
  { path: "/gallery-details/:category", name: "gallery-details", apis: ["gallery"], element: <GalleryDetails /> },
];


function App() {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const state = useSelector(state => state);

  const getPriorityApis = () => {
    const matchedPage = pageList.find(page =>
      matchPath({ path: page.path, end: true }, pathname)
    );
    return matchedPage?.apis || [];
  };

  useDebouncedEffect(() => {
    const priority = getPriorityApis();
    priority.forEach(apiName => {
      const apiObj = apiList.find(a => a.name === apiName);
      if (apiObj && apiObj.selector(state) === 'idle') {
        dispatch(apiObj.api());
      }
    });
  }, 50, [pathname, dispatch]);

  useDebouncedEffect(() => {
    const priority = getPriorityApis();
    const timer = setTimeout(() => {
      apiList.forEach(apiObj => {
        if (!priority.includes(apiObj.name) && apiObj.selector(state) === 'idle') {
          dispatch(apiObj.api());
        }
      });
    }, 1000);
    return () => clearTimeout(timer);
  }, 50, [pathname, dispatch]);

  return (
    <ThemeProvider theme={theme}>
      <Navbar />
      <Routes>
        {pageList.map((page, index) => (
          <Route key={index} path={page.path} element={page.element} />
        ))}
      </Routes>
      <Footer />
      <SmoothScroll />
      <CssBaseline />
      <ScrollToTop pathname={pathname} />
    </ThemeProvider>
  );
}

export default App;
